/**
 * Die Grundeinstellungen von Kora – ein Baustein des Admin Centers.
 *
 * Alles hier wird einmal für die Firma festgelegt und nicht je Benutzer:
 * Taktung des Nachfassens, Kalkulationsaufschläge, Textvorlagen, Signaturen,
 * Nummernkreis und die geltende AGB.
 *
 * War früher ein eigenes Fenster neben dem Admin Center. Zwei Orte für
 * dieselbe Art von Entscheidung waren einer zu viel – man musste wissen, was
 * wo liegt, und das weiß niemand.
 *
 * Bewusst nicht dabei: die Zugangsdaten der angebundenen Systeme. Sie
 * gehören in die Konfiguration des Servers und nicht in eine Maske, in der sich
 * jemand vertippen kann.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from '../api';
import { useKora, useMeta } from '../zustand';
import { SevdeskBlock } from './SevdeskBlock';
import type {
  EinstellungenAenderung,
  EinstellungenStand,
  NummernHerkunft,
  Signatur,
  VorlagenVorschau,
} from '../typen';
import './einstellungen.css';

export function Grundeinstellungen({ onSchliessen }: { onSchliessen?: () => void } = {}) {
  const { toastZeigen } = useKora();
  const meta = useMeta();
  const queryClient = useQueryClient();

  const stand = useQuery({
    queryKey: ['einstellungen'],
    queryFn: () => api.hole<EinstellungenStand>('/einstellungen'),
  });

  const [vorlKat, setVorlKat] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [vorlageGeladen, setVorlageGeladen] = useState('');

  const vorschau = useQuery({
    queryKey: ['vorlagen-vorschau', vorlKat],
    queryFn: () =>
      api.hole<VorlagenVorschau>(
        `/einstellungen/vorschau${vorlKat ? `?prefix=${encodeURIComponent(vorlKat)}` : ''}`,
      ),
  });

  // Vorlagenfelder auf die gewählte Kategorie umstellen.
  useEffect(() => {
    if (!vorschau.data) return;
    const schluessel = vorlKat || '__global__';
    if (vorlageGeladen === schluessel) return;
    setBetreff(vorschau.data.vorlage.betreff);
    setText(vorschau.data.vorlage.text);
    setVorlageGeladen(schluessel);
  }, [vorschau.data, vorlKat, vorlageGeladen]);

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['einstellungen'] });
    void queryClient.invalidateQueries({ queryKey: ['vorlagen-vorschau'] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  }

  const global = useMutation({
    mutationFn: (daten: EinstellungenAenderung) => api.aendere('/einstellungen', daten),
    onSuccess: neuLaden,
    /*
     * Ohne diese Meldung bliebe eine abgewiesene Eingabe stumm: Der Server
     * lehnt etwa „Gelb ab 10, Rot ab 7" ab, das Feld zeigte aber weiter die
     * eingetippte Zahl. Wer nicht neu lädt, hält sie für gespeichert.
     */
    onError: (f) => {
      toastZeigen(f instanceof Error ? f.message : 'Einstellung nicht gespeichert.');
      neuLaden();
    },
  });

  const kategorie = useMutation({
    mutationFn: ({
      prefix,
      ...daten
    }: {
      prefix: string;
      warn?: number;
      krit?: number;
      aktiv?: boolean;
      aufschlag?: number;
    }) =>
      api.ersetze(`/einstellungen/kategorie/${prefix}`, daten),
    onSuccess: neuLaden,
  });

  const vorlageSpeichern = useMutation({
    mutationFn: () =>
      vorlKat
        ? api.ersetze(`/einstellungen/vorlage/${vorlKat}`, { betreff1: betreff, text1: text })
        : api.aendere('/einstellungen', { betreff1: betreff, text1: text }),
    onSuccess: () => {
      neuLaden();
      toastZeigen('Textvorlage gespeichert.');
    },
  });

  const vorlageZuruecksetzen = useMutation({
    mutationFn: () => api.loesche(`/einstellungen/vorlage/${vorlKat}`),
    onSuccess: () => {
      setVorlageGeladen('');
      neuLaden();
      toastZeigen('Eigene Vorlage entfernt – die Kategorie erbt wieder die Standardvorlage.');
    },
  });

  const d = stand.data;

  return (
    <>
      {!d && <div className="modal-hinweis">Einstellungen werden geladen …</div>}

      {d && (
        <>
          {/* ---------- Taktung ---------- */}
          <section className="ein-abschnitt">
            <div className="ein-titel">Globale Taktung</div>

            <div className="ein-raster-2">
              <label className="ein-feld">
                <span>Nachfassen fällig nach … Tagen</span>
                <input
                  type="number"
                  className="ein-eingabe ein-zahl"
                  defaultValue={d.warn}
                  min={1}
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (w > 0 && w !== d.warn) global.mutate({ warn: w });
                  }}
                />
              </label>
              <label className="ein-feld">
                <span>Anruf fällig nach … Tagen</span>
                <input
                  type="number"
                  className="ein-eingabe ein-zahl"
                  defaultValue={d.krit}
                  min={1}
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (w > 0 && w !== d.krit) global.mutate({ krit: w });
                  }}
                />
              </label>
            </div>

            <div className="ein-hinweis">
              Gezählt ab dem Eintrag „Gesendet". Dieselbe Schwelle färbt die Ampel in den Listen.
            </div>

            {/*
              Eskalationsstufen für „Meine Aufgaben".
              Zentral und nicht je Benutzer: Wie dringend etwas ist, entscheidet
              nicht der, der es ansieht – sonst sähe dieselbe Liste bei zwei
              Personen verschieden aus.
            */}
            <div className="ein-titel" style={{ marginTop: 22 }}>
              Farben in „Meine Aufgaben"
            </div>
            <div className="ein-raster-2">
              <label className="ein-feld">
                <span>Grün bis … Tage beim Kunden</span>
                <input
                  type="number"
                  className="ein-eingabe ein-zahl"
                  defaultValue={d.stufeGruen}
                  min={0}
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (w >= 0 && w !== d.stufeGruen) global.mutate({ stufeGruen: w });
                  }}
                />
              </label>
              <label className="ein-feld">
                <span>Orange bis … Tage beim Kunden</span>
                <input
                  type="number"
                  className="ein-eingabe ein-zahl"
                  defaultValue={d.stufeOrange}
                  min={1}
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (w > 0 && w !== d.stufeOrange) global.mutate({ stufeOrange: w });
                  }}
                />
              </label>
            </div>

            <div className="ein-hinweis">
              Darüber wird die Zeile rot. Es sind Farben, keine Fristen – sie sagen, wie lange ein
              Angebot beim Kunden liegt, nicht dass jemand etwas versäumt hat. Grün muss vor Orange
              enden, sonst wäre das orange Band nie zu sehen. Der Ring „Beim Kunden in Entscheidung"
              teilt sich nach denselben Zahlen: kritisch ist eine Woche nach Orange.
            </div>

            {/*
              Die Anrufzeiten der Uhr auf „Meine Aufgaben". Sie stehen hier und
              nicht im Code: Bauservice ruft anders an als Innentüren, und die
              Zeiten verschieben sich mit der Jahreszeit.
            */}
            <div className="ein-titel">Anrufzeiten</div>
            <div className="ein-reihe">
              {(
                [
                  ['anrufVon', 'Anrufe ab … Uhr'],
                  ['anrufMittagAb', 'Mittagspause ab'],
                  ['anrufMittagBis', 'Mittagspause bis'],
                  ['anrufLetzteAb', 'Letztes Fenster ab'],
                  ['anrufBis', 'Schluss um … Uhr'],
                ] as const
              ).map(([feld, label]) => (
                <label className="ein-feld" key={feld}>
                  <span>{label}</span>
                  <input
                    type="number"
                    className="ein-eingabe ein-zahl"
                    defaultValue={d[feld]}
                    min={0}
                    max={24}
                    onBlur={(e) => {
                      const w = Number(e.target.value);
                      if (w >= 0 && w !== d[feld]) global.mutate({ [feld]: w });
                    }}
                  />
                </label>
              ))}
            </div>

            <div className="ein-hinweis">
              Ganze Stunden. Die Uhr auf „Meine Aufgaben" sagt daraus, ob gerade eine gute Zeit zum
              Anrufen ist. Am Wochenende bleibt sie stumm. Die Zeiten müssen aufsteigen – eine
              Mittagspause nach Feierabend gäbe ein Fenster, das es nie gibt.
            </div>

            {/*
              Die Schwelle der Chat-Mail gehört hierher und nicht in den Code
              (TEAM-CHAT.md, Abschnitt 4). Sie ist eine Betriebsentscheidung:
              Wie lange darf jemand weg sein, bevor ihn eine Nachricht auch
              per Outlook erreicht?
            */}
            <div className="ein-titel">Nachrichten</div>
            <div className="ein-reihe">
              <label className="ein-feld">
                <span>Mail erst nach … Minuten ohne Kora</span>
                <input
                  type="number"
                  className="ein-eingabe ein-zahl"
                  defaultValue={d.chatMailAbMinuten}
                  min={1}
                  max={240}
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (w >= 1 && w !== d.chatMailAbMinuten) global.mutate({ chatMailAbMinuten: w });
                  }}
                />
              </label>
            </div>
            <div className="ein-hinweis">
              Wer im Werkzeug sitzt, bekommt keine Mail über etwas, das er auf dem Schirm hat – der
              Zähler am Nachrichten-Symbol reicht ihm. Erst nach dieser Zeit ohne Kora geht eine
              Outlook-Nachricht hinaus. Unabhängig davon kommt morgens eine Sammelmail über alles
              Ungelesene.
            </div>

            {/*
              Hängerregeln und Vorbelegungen der Auftragsabwicklung. Dieselben
              Tagesgrenzen färben die Liste und lassen Kora dort sprechen –
              eine Quelle, nicht zwei.
            */}
            <div className="ein-titel">Auftragsabwicklung</div>
            <div className="ein-reihe">
              {(
                [
                  ['abwAbTage', 'Keine AB nach … Tagen'],
                  ['abwRetourTage', 'AB nicht retour nach … Tagen'],
                  ['abwMontageVorlauf', 'Montage planen … Tage vor Lieferung'],
                ] as const
              ).map(([feld, label]) => (
                <label className="ein-feld" key={feld}>
                  <span>{label}</span>
                  <input
                    type="number"
                    className="ein-eingabe ein-zahl"
                    defaultValue={d[feld]}
                    min={0}
                    max={120}
                    onBlur={(e) => {
                      const w = Number(e.target.value);
                      if (w >= 0 && w !== d[feld]) global.mutate({ [feld]: w });
                    }}
                  />
                </label>
              ))}
            </div>
            {/*
              Wie lange ein Wartezustand ruhen darf (v12, Punkt 7).

              Warten ist sichtbar, aber ruhig: Der Vorgang steht kursiv in der
              Wiedervorlage, ohne Knopf, mit „ruht bis …". Reißt die Frist, ist
              es kein Warten mehr, sondern eine Aufgabe – dann wandert er in
              die Kachel jener Tätigkeit, die ihn löst, und trägt einen Chip.
              Die Zahlen gehören dem Betrieb, nicht dem Code.
            */}
            <div className="ein-titel ein-titel-klein">Wie lange gewartet werden darf</div>
            <div className="ein-reihe">
              {(
                [
                  ['retour', 'AB retour – Lieferant bestätigt', 'löst „AB nachfragen" in der Beschaffung aus'],
                  ['produktion', 'In Produktion – Lieferant produziert', 'löst „Termin nachfragen" in Lieferung & Montage aus'],
                  ['montage', 'Montage terminiert – Termin steht', 'löst „Termin nachfragen" in Lieferung & Montage aus'],
                ] as const
              ).map(([schritt, label, wirkung]) => (
                <label className="ein-feld" key={schritt} title={wirkung}>
                  <span>{label}</span>
                  <input
                    type="number"
                    className="ein-eingabe ein-zahl"
                    defaultValue={d.abwFristen?.[schritt] ?? ''}
                    min={1}
                    max={365}
                    onBlur={(e) => {
                      const w = Number(e.target.value);
                      if (w >= 1 && w !== d.abwFristen?.[schritt]) {
                        global.mutate({ abwFristen: { ...(d.abwFristen ?? {}), [schritt]: w } });
                      }
                    }}
                  />
                </label>
              ))}
            </div>
            <div className="ein-hinweis">
              Drei Schritte der Kette sind Warten auf andere; die übrigen sechs sind Tätigkeiten
              und ruhen nie. Was hier steht, entscheidet, ab wann aus dem Warten eine Aufgabe wird
              – und in welcher Kachel sie dann auftaucht.
            </div>

            <div className="ein-hinweis">
              Rot wird eine Bestellung ohne AB nach der ersten Frist, eine liegende AB nach der
              zweiten; Terrakotta heißt „Montage planen", sobald die Lieferung im Vorlauf liegt. In
              der Abwicklung ist der Kalkulant des Angebots für jede Bestellung verantwortlich – er
              wird beim Anlegen des Angebots festgelegt und in der Kundenkarte des Vorgangs gewechselt.
            </div>

            <div className="ein-schalterzeile">
              <div>Eigene Taktung pro Kategorie</div>
              <button
                type="button"
                className={`ein-schalter ${d.proKategorie ? 'an' : ''}`}
                onClick={() => global.mutate({ proKategorie: !d.proKategorie })}
              >
                <span />
              </button>
            </div>

            {d.proKategorie && (
              <div className="ein-kattabelle">
                <div className="ein-katzeile ein-katkopf">
                  <div>Kategorie</div>
                  <div>1. Erinnerung</div>
                  <div>Anruf ab</div>
                  <div>Offen</div>
                  <div style={{ textAlign: 'right' }}>Aktiv</div>
                </div>
                {d.kategorien.map((c) => (
                  <div
                    className="ein-katzeile"
                    key={c.prefix}
                    style={{ opacity: c.aktiv ? 1 : 0.55 }}
                  >
                    <div className="ein-katname">
                      <span className="ein-katprefix">{c.prefix}</span>
                      <span>{c.label}</span>
                    </div>
                    <input
                      type="number"
                      className="ein-katfeld ein-zahl"
                      defaultValue={c.warn}
                      min={1}
                      onBlur={(e) => {
                        const w = Number(e.target.value);
                        if (w > 0 && w !== c.warn) kategorie.mutate({ prefix: c.prefix, warn: w });
                      }}
                    />
                    <input
                      type="number"
                      className="ein-katfeld ein-zahl"
                      defaultValue={c.krit}
                      min={1}
                      onBlur={(e) => {
                        const w = Number(e.target.value);
                        if (w > 0 && w !== c.krit) kategorie.mutate({ prefix: c.prefix, krit: w });
                      }}
                    />
                    <div className="ein-katanzahl">{c.anzahl}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className={`ein-schalter klein ${c.aktiv ? 'an' : ''}`}
                        onClick={() => kategorie.mutate({ prefix: c.prefix, aktiv: !c.aktiv })}
                      >
                        <span />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/*
              Es gehen keine Mails von selbst hinaus.
              Hier stand ein gruener Kasten "wird automatisch versendet" und
              ein Schalter zum Pausieren der Automatik. Beides beschrieb einen
              Zustand, den es nicht mehr gibt: Die Erinnerung oeffnet der
              Bearbeiter selbst, sieht den Text und schickt ihn ab. Ein
              Schalter, der nichts mehr schaltet, ist schlimmer als keiner.
            */}
            <div className="ein-hinweis">
              Die Zahlen bestimmen, wann ein Vorgang in „Meine Aufgaben“ wieder auftaucht und
              wann die Ampel im Cockpit umspringt. Versendet wird nichts von selbst – jede
              Erinnerung öffnet der Bearbeiter, sieht sie und schickt sie ab.
            </div>
          </section>

          {/* ---------- Kalkulationsaufschläge ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Kalkulationsaufschläge</div>

            <div className="ein-hinweis">
              Grundlage der Gewinnprognose: Aus einem erteilten Auftrag wird nicht sein voller Wert
              zum Gewinn, sondern der Deckungsbeitrag. Ohne hinterlegten Aufschlag rechnet KORA für
              die Kategorie mit null – lieber vorsichtig als erfunden.
            </div>

            <label className="ein-feldzeile">
              <span>Standardaufschlag, wenn für eine Kategorie keiner hinterlegt ist</span>
              <span className="ein-eingabezeile">
                <input
                  type="number"
                  className="ein-katfeld ein-zahl"
                  defaultValue={d.aufschlagStandard || ''}
                  min={0}
                  max={500}
                  placeholder="0"
                  onBlur={(e) => {
                    const w = Number(e.target.value) || 0;
                    if (w !== d.aufschlagStandard) global.mutate({ aufschlagStandard: w });
                  }}
                />
                <span className="ein-einheit">
                  % Aufschlag
                  {d.margeStandardText ? ` · entspricht ${d.margeStandardText} Marge` : ''}
                </span>
              </span>
            </label>

            <div className="ein-kattabelle">
              <div className="ein-katzeile ein-katkopf ein-margenzeile">
                <div>Kategorie</div>
                <div>Aufschlag</div>
                <div>Marge</div>
              </div>
              {d.kategorien.map((c) => (
                <div className="ein-katzeile ein-margenzeile" key={c.prefix}>
                  <div className="ein-katname">
                    <span className="ein-katprefix">{c.prefix}</span>
                    <span>{c.label}</span>
                  </div>
                  <input
                    type="number"
                    className="ein-katfeld ein-zahl"
                    defaultValue={c.aufschlag || ''}
                    min={0}
                    max={500}
                    placeholder={d.aufschlagStandard ? String(d.aufschlagStandard) : '—'}
                    onBlur={(e) => {
                      const w = Number(e.target.value) || 0;
                      if (w !== c.aufschlag) kategorie.mutate({ prefix: c.prefix, aufschlag: w });
                    }}
                  />
                  <div className="ein-katanzahl">
                    {c.margeText || (d.margeStandardText ? `(${d.margeStandardText})` : '—')}
                  </div>
                </div>
              ))}
            </div>

            <div className="ein-hinweis">
              <strong>Aufschlag ist nicht Marge.</strong> 30 % Aufschlag auf den Einkauf ergeben
              23,1 % Marge vom Verkaufspreis – aus 100 € Einkauf werden 130 € Verkauf mit 30 €
              Deckungsbeitrag. KORA rechnet das um; eingetragen wird der Aufschlag, weil so
              kalkuliert wird.
            </div>
          </section>

          {/* ---------- Finanzampeln ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Ampeln der Finanzübersicht</div>

            <div className="ein-hinweis">
              Jede Karte in der Finanzübersicht schreibt ihre Regel hin – „Grün ab 22 % Marge".
              Damit das keine Behauptung bleibt, stehen die Schwellen hier und nicht im Programm.
              Grün muss jeweils über Gelb liegen; darunter ist rot.
            </div>

            <label className="ein-feldzeile">
              <span>Marge grün ab</span>
              <span className="ein-eingabezeile">
                <input
                  type="number"
                  className="ein-katfeld ein-zahl"
                  defaultValue={d.finanzampeln.margeGruen}
                  min={0}
                  max={100}
                  onBlur={(e) => {
                    const w = Number(e.target.value) || 0;
                    if (w !== d.finanzampeln.margeGruen) global.mutate({ margeGruen: w });
                  }}
                />
                <span className="ein-einheit">% Marge</span>
              </span>
            </label>

            <label className="ein-feldzeile">
              <span>Marge gelb ab</span>
              <span className="ein-eingabezeile">
                <input
                  type="number"
                  className="ein-katfeld ein-zahl"
                  defaultValue={d.finanzampeln.margeGelb}
                  min={0}
                  max={100}
                  onBlur={(e) => {
                    const w = Number(e.target.value) || 0;
                    if (w !== d.finanzampeln.margeGelb) global.mutate({ margeGelb: w });
                  }}
                />
                <span className="ein-einheit">% Marge · darunter rot</span>
              </span>
            </label>

            <label className="ein-feldzeile">
              <span>Verlustmonate rot ab</span>
              <span className="ein-eingabezeile">
                <input
                  type="number"
                  className="ein-katfeld ein-zahl"
                  defaultValue={d.finanzampeln.verlustRot}
                  min={1}
                  max={12}
                  onBlur={(e) => {
                    const w = Number(e.target.value) || 1;
                    if (w !== d.finanzampeln.verlustRot) global.mutate({ verlustRot: w });
                  }}
                />
                <span className="ein-einheit">Monaten mit Verlust</span>
              </span>
            </label>

            <label className="ein-feldzeile">
              <span>Auftragspolster grün ab</span>
              <span className="ein-eingabezeile">
                <input
                  type="number"
                  className="ein-katfeld ein-zahl"
                  defaultValue={d.finanzampeln.polsterGruen}
                  min={0}
                  step={1000}
                  onBlur={(e) => {
                    const w = Number(e.target.value) || 0;
                    if (w !== d.finanzampeln.polsterGruen) global.mutate({ polsterGruen: w });
                  }}
                />
                <span className="ein-einheit">€ Deckungsbeitrag</span>
              </span>
            </label>

            <label className="ein-feldzeile">
              <span>Auftragspolster gelb ab</span>
              <span className="ein-eingabezeile">
                <input
                  type="number"
                  className="ein-katfeld ein-zahl"
                  defaultValue={d.finanzampeln.polsterGelb}
                  min={0}
                  step={1000}
                  onBlur={(e) => {
                    const w = Number(e.target.value) || 0;
                    if (w !== d.finanzampeln.polsterGelb) global.mutate({ polsterGelb: w });
                  }}
                />
                <span className="ein-einheit">€ · darunter rot</span>
              </span>
            </label>

            <div className="ein-hinweis">
              Das Auftragspolster ist der Deckungsbeitrag aus erteilten Aufträgen, die noch nicht
              abgerechnet sind – erwartet, nicht gebucht. Er fließt nie in die Ist-Zahlen ein.
            </div>
          </section>

          {/* ---------- Angebotsprüfung ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Angebotsprüfung</div>

            <div className="ein-schalterzeile">
              <div>Angebotsprüfung anbieten</div>
              <button
                type="button"
                className={`ein-schalter ${d.pruefungAktiv ? 'an' : ''}`}
                onClick={() => global.mutate({ pruefungAktiv: !d.pruefungAktiv })}
              >
                <span />
              </button>
            </div>

            <div className="ein-hinweis">
              Ausgeschaltet sieht niemand etwas davon: Der Prüfblock erscheint nicht im
              Angebot, und kein Statuswechsel hängt daran – Angebote lassen sich ganz
              normal versenden.
              <br />
              <br />
              Eingeschaltet erscheint der Block bei jedem Angebot. Geprüft wird trotzdem
              nur dort, wo für das Gewerk eine Checkliste hinterlegt ist und die KI
              erreichbar ist; sonst bleibt er eine Anzeige ohne Folgen.
            </div>
          </section>

          {/* ---------- Ordnerstruktur ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Ordnerstruktur der Ablage</div>

            <div className="ein-schalterzeile">
              <div>Erklärtexte in neue Ordner legen</div>
              <button
                type="button"
                className={`ein-schalter ${d.ordnertexte ? 'an' : ''}`}
                onClick={() => global.mutate({ ordnertexte: !d.ordnertexte })}
              >
                <span />
              </button>
            </div>

            <div className="ein-hinweis">
              Kora legt in jeden neuen Vorgangsordner eine Datei
              „_Was gehört hier hinein.txt", die sagt, was dort abzulegen ist – und was
              nicht und wohin stattdessen. Eine Übergangshilfe: Sitzt die Struktur bei
              allen, schaltest du sie hier ab.
              <br />
              <br />
              Das wirkt nur auf <strong>neue</strong> Vorgänge. Bereits kopierte Dateien
              bleiben liegen, wo sie sind – eine Kopie lässt sich nachträglich nicht
              ändern, genau wie bei der AGB.
            </div>
          </section>

          {/* ---------- sevDesk (v12) ---------- */}
          <SevdeskBlock onSchliessen={onSchliessen} />

          {/* ---------- Textvorlage ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Textvorlage der Erinnerung</div>

            <div className="ein-hinweis">
              Der Vorschlag, der im Erinnerungsfenster steht. Er wird vor dem Senden gezeigt und
              lässt sich dort noch ändern – wer den Vorgang führt, weiß oft mehr als die
              Vorlage. Versendet wird aus dem Postfach des Versenders und mit seiner Signatur.
            </div>

            <div className="ein-vorlagenkopf">
              <label className="ein-feld">
                <span>Gilt für</span>
                <select
                  className="ein-eingabe ein-breit"
                  value={vorlKat}
                  onChange={(e) => {
                    setVorlKat(e.target.value);
                    setVorlageGeladen('');
                  }}
                >
                  <option value="">Alle Kategorien (Standardvorlage)</option>
                  {meta.kategorien.map((c) => (
                    <option key={c.prefix} value={c.prefix}>
                      {c.prefix} · {c.label}
                    </option>
                  ))}
                </select>
              </label>

              {vorlKat && d.katVorlagen[vorlKat] && (
                <button
                  type="button"
                  className="ein-knopf-sek"
                  onClick={() => vorlageZuruecksetzen.mutate()}
                >
                  Eigene Vorlage entfernen
                </button>
              )}

              <div className="ein-herkunft">
                {!vorlKat
                  ? 'Gilt für alle Kategorien ohne eigene Vorlage.'
                  : d.katVorlagen[vorlKat]
                    ? 'Eigene Vorlage für diese Kategorie.'
                    : 'Erbt aktuell die Standardvorlage – Speichern legt eine eigene an.'}
              </div>
            </div>

            <label className="ein-feld">
              <span>Betreff</span>
              <input
                className="ein-eingabe"
                value={betreff}
                onChange={(e) => setBetreff(e.target.value)}
              />
            </label>

            <label className="ein-feld">
              <span>Text</span>
              <textarea
                className="ein-eingabe ein-textarea"
                rows={9}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </label>

            <div className="ein-platzhalter">
              <span className="ein-hinweis">Platzhalter:</span>
              {meta.platzhalter.map((p) => (
                <span className="ein-platz" key={p}>
                  {p}
                </span>
              ))}
            </div>

            <div className="ein-vorlagen-aktionen">
              <button
                type="button"
                className="ein-knopf-prim"
                disabled={vorlageSpeichern.isPending}
                onClick={() => vorlageSpeichern.mutate()}
              >
                {vorlageSpeichern.isPending ? 'Wird gespeichert …' : 'Vorlage speichern'}
              </button>
            </div>

            <div className="ein-vorschau">
              <div className="ein-vorschau-kopf">
                <div className="ein-vorschau-titel">So sieht die Mail aus</div>
                <div className="ein-vorschau-bezug">
                  {vorschau.data?.leer ? 'Noch kein Angebot' : `Beispieldaten · ${vorschau.data?.bezug ?? ''}`}
                </div>
              </div>

              {vorschau.data?.leer ? (
                <div className="ein-vorschau-blatt">
                  <span className="ein-hinweis">{vorschau.data.hinweis}</span>
                </div>
              ) : (
                vorschau.data && (
                  <div className="ein-vorschau-blatt">
                    <div className="mail-kopfzeile">
                      <span>Von</span>
                      <span>{vorschau.data.von}</span>
                    </div>
                    <div className="mail-kopfzeile">
                      <span>An</span>
                      <span>{vorschau.data.an}</span>
                    </div>
                    <div className="mail-betreff">{vorschau.data.betreff}</div>
                    <div className="mail-text">{vorschau.data.text}</div>
                    <div className="mail-signatur">
                      {vorschau.data.signaturHtml ? (
                        <div dangerouslySetInnerHTML={{ __html: vorschau.data.signaturHtml }} />
                      ) : (
                        <span className="mail-signatur-leer">
                          Keine Signatur für {vorschau.data.versender ?? '—'} hinterlegt.
                        </span>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </section>

          <Nummernkreis />

          <Signaturen />

          {/* ---------- Anmeldung ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Anmeldung</div>
            <div className="eh-banner eh-banner--blau">
              {meta.demoModus ? (
                <span>
                  Kora läuft im Demo-Modus: die Personenauswahl meldet ohne Prüfung an. Für den
                  Echtbetrieb in der Konfiguration <code>AUTH_MODE=entra</code> setzen – dann läuft
                  die Anmeldung über das Microsoft-Konto von Eichenherz.
                </span>
              ) : (
                <span>
                  Die Anmeldung läuft über Microsoft Entra ID. Kora speichert kein eigenes Passwort –
                  Passwörter, Zwei-Faktor und Sperren verwaltet Microsoft. Wer Zugang bekommt, legt
                  die Administration im Admin Center fest.
                </span>
              )}
            </div>
          </section>

          <Agb daten={d} />
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Nummernkreis der Angebote.
 *
 * Im Regelfall ermittelt Kora die höchste vergebene Nummer selbst – aus dem
 * eigenen Bestand und aus den Ablageordnern „Angebote" und „Aufträge".
 * Die manuelle Eingabe ist der Notnagel, falls die Ablage nicht erreichbar ist
 * oder der Bestand woanders liegt.
 */
function Nummernkreis() {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [eingabe, setEingabe] = useState('');
  const [fehler, setFehler] = useState('');
  const [bearbeiten, setBearbeiten] = useState(false);

  const abfrage = useQuery({
    queryKey: ['nummernkreis'],
    queryFn: () => api.hole<NummernHerkunft>('/einstellungen/nummernkreis'),
  });

  const speichern = useMutation({
    mutationFn: (wert: number | null) =>
      api.ersetze<NummernHerkunft>('/einstellungen/nummernkreis', { letzteNummer: wert }),
    onSuccess: (neu) => {
      queryClient.setQueryData(['nummernkreis'], neu);
      void queryClient.invalidateQueries({ queryKey: ['naechste-angebotsnummer'] });
      setBearbeiten(false);
      setEingabe('');
      setFehler('');
      toastZeigen(`Nächste Angebotsnummer: ${neu.naechste}`);
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  const d = abfrage.data;

  /**
   * Wird die eingetippte Nummer überhaupt wirksam?
   *
   * Kora nimmt die höchste aus allen Quellen. Eine niedrigere Eingabe bleibt
   * deshalb wirkungslos – und zwar stillschweigend, was die schlechteste Art
   * von Fehlverhalten ist: Man setzt etwas, bekommt eine Bestätigung, und
   * nichts ändert sich. Deshalb wird hier vorher benannt, was blockiert.
   */
  const gewuenscht = eingabe.length === 5 ? Number(eingabe) : null;
  const blockade = (() => {
    if (!d || gewuenscht === null) return null;
    const andere: { quelle: string; wert: number }[] = [
      { quelle: 'Der Bestand in Kora', wert: d.ausDatenbank ?? 0 },
      { quelle: 'Die Ablage', wert: (d.ausSharepoint ?? d.ausZwischenspeicher) ?? 0 },
    ];
    const hoechste = andere.reduce((a, b) => (b.wert > a.wert ? b : a));
    return hoechste.wert > gewuenscht ? hoechste : null;
  })();

  return (
    <section className="ein-abschnitt ein-trenner">
      <div className="ein-titel">Nummernkreis der Angebote</div>

      {abfrage.isLoading && <div className="modal-hinweis">Wird ermittelt …</div>}
      {abfrage.isError && (
        <div className="eh-banner eh-banner--gelb">
          {abfrage.error instanceof Error ? abfrage.error.message : 'Nicht ermittelbar.'}
        </div>
      )}

      {d && (
        <>
          <div className="ein-nummer-gross">
            <span className="ein-nummer-wert">{d.naechste}</span>
            <span className="ein-nummer-text">
              bekommt das nächste Angebot · Format JJNNN, Zähler startet jedes Jahr neu bei 001
            </span>
          </div>

          <div className="ein-nummer-quellen">
            <Quelle label="Bestand in Kora" wert={d.ausDatenbank} />
            <Quelle
              label="Ordner der Ablage"
              wert={d.ausSharepoint ?? d.ausZwischenspeicher}
              zusatz={
                d.sharepointGelesen
                  ? 'Angebote und Aufträge, soeben gelesen'
                  : 'zuletzt erkannter Stand'
              }
            />
            <Quelle label="Manuell gesetzt" wert={d.ausEinstellung} />
          </div>

          {d.hinweis && <div className="eh-banner eh-banner--gelb">{d.hinweis}</div>}

          {!bearbeiten ? (
            <div className="ein-vorlagen-aktionen">
              <button type="button" className="ein-knopf-sek" onClick={() => setBearbeiten(true)}>
                Zähler von Hand setzen
              </button>
              {d.ausEinstellung !== null && (
                <button
                  type="button"
                  className="ein-knopf-sek"
                  onClick={() => speichern.mutate(null)}
                >
                  Manuelle Vorgabe entfernen
                </button>
              )}
            </div>
          ) : (
            <>
              <label className="ein-feld">
                <span>Zuletzt vergebene Angebotsnummer</span>
                <input
                  className="ein-eingabe ein-zahl"
                  inputMode="numeric"
                  placeholder={String(d.naechste - 1)}
                  value={eingabe}
                  onChange={(e) => {
                    setEingabe(e.target.value.replace(/[^0-9]/g, '').slice(0, 5));
                    setFehler('');
                  }}
                />
                <span className="ein-hinweis">
                  Kora zählt ab hier weiter. Der Wert wirkt als Untergrenze: Eine höhere Nummer aus
                  dem Bestand oder aus der Ablage hat Vorrang.
                </span>
                {blockade && (
                  <span className="ein-hinweis ein-hinweis--warnung">
                    Diese Nummer wird nicht greifen: {blockade.quelle} enthält bereits{' '}
                    {blockade.wert}, das nächste Angebot bekäme weiterhin {blockade.wert + 1}. Eine
                    niedrigere Nummer lässt sich nicht erzwingen – sonst entstünde eine doppelt
                    vergebene Nummer. Wenn der hohe Wert ein Versehen war, etwa ein vertippter
                    Ordnername, muss er dort korrigiert werden; danach zählt Kora von allein
                    richtig weiter.
                  </span>
                )}
              </label>
              <div className="ein-vorlagen-aktionen">
                <button
                  type="button"
                  className="ein-knopf-prim"
                  disabled={eingabe.length !== 5 || speichern.isPending}
                  onClick={() => speichern.mutate(Number(eingabe))}
                >
                  {speichern.isPending ? 'Wird gesetzt …' : 'Übernehmen'}
                </button>
                <button
                  type="button"
                  className="ein-knopf-sek"
                  onClick={() => {
                    setBearbeiten(false);
                    setEingabe('');
                    setFehler('');
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </>
          )}

          {fehler && <div className="modal-fehler">{fehler}</div>}
        </>
      )}
    </section>
  );
}

function Quelle({
  label,
  wert,
  zusatz,
}: {
  label: string;
  wert: number | null;
  zusatz?: string;
}) {
  return (
    <div className="ein-quelle">
      <span className="ein-quelle-label">{label}</span>
      <span className="ein-quelle-wert">{wert ?? '—'}</span>
      {zusatz && wert !== null && <span className="ein-quelle-zusatz">{zusatz}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function Signaturen() {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [person, setPerson] = useState<string | null>(null);
  const [html, setHtml] = useState('');
  const [geladen, setGeladen] = useState<string | null>(null);

  const abfrage = useQuery({
    queryKey: ['signaturen'],
    queryFn: () => api.hole<Signatur[]>('/einstellungen/signaturen'),
  });

  const liste = abfrage.data ?? [];
  const aktuell = person ?? liste[0]?.name ?? null;

  useEffect(() => {
    if (!aktuell || geladen === aktuell) return;
    const s = liste.find((x) => x.name === aktuell);
    if (!s) return;
    setHtml(s.html);
    setGeladen(aktuell);
  }, [aktuell, geladen, liste]);

  const speichern = useMutation({
    mutationFn: () => api.ersetze(`/einstellungen/signaturen/${aktuell}`, { html }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['signaturen'] });
      void queryClient.invalidateQueries({ queryKey: ['vorlagen-vorschau'] });
      toastZeigen(`Signatur von ${aktuell} gespeichert.`);
    },
  });

  return (
    <section className="ein-abschnitt ein-trenner">
      <div className="ein-titel">Signaturen</div>
      <div className="ein-hinweis">
        HTML-Signatur pro Person. Unter der Mail steht die Signatur des <strong>Versenders</strong> –
        das ist nicht zwingend der Kalkulant.
      </div>

      <div className="ein-signaturen">
        <div className="ein-sigliste">
          {liste.map((p) => (
            <button
              key={p.name}
              type="button"
              className={`ein-sigperson ${aktuell === p.name ? 'aktiv' : ''}`}
              onClick={() => {
                setPerson(p.name);
                setGeladen(null);
              }}
            >
              <span className="eh-avatar ein-sigavatar">
                {p.name.slice(0, 2).toUpperCase()}
              </span>
              <span className="ein-sigtext">
                <span className="ein-signame">{p.name}</span>
                <span className="ein-sigstatus">
                  {p.hinterlegt ? 'Signatur hinterlegt' : 'keine Signatur'}
                </span>
              </span>
            </button>
          ))}
        </div>

        <div className="ein-sigeditor">
          <label className="ein-feld">
            <span>HTML-Signatur · {aktuell ?? '—'}</span>
            <textarea
              className="ein-eingabe ein-code"
              rows={7}
              value={html}
              placeholder="<table>…</table> oder <div>…</div>"
              onChange={(e) => setHtml(e.target.value)}
            />
          </label>

          <div className="ein-sigaktionen">
            <label className="ein-knopf-sek">
              HTML-Datei hochladen
              <input
                type="file"
                accept=".html,.htm,text/html"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const datei = e.target.files?.[0];
                  if (!datei) return;
                  const leser = new FileReader();
                  leser.onload = () => setHtml(String(leser.result));
                  leser.readAsText(datei);
                  e.target.value = '';
                }}
              />
            </label>
            <button type="button" className="ein-knopf-sek" onClick={() => setHtml('')}>
              Signatur entfernen
            </button>
            <button
              type="button"
              className="ein-knopf-prim"
              disabled={!aktuell || speichern.isPending}
              onClick={() => speichern.mutate()}
            >
              {speichern.isPending ? 'Wird gespeichert …' : 'Signatur speichern'}
            </button>
          </div>

          <div className="ein-sigvorschau">
            <div className="ein-vorschau-titel">Vorschau</div>
            <div className="ein-sigvorschau-blatt">
              {html ? (
                <div dangerouslySetInnerHTML={{ __html: html }} />
              ) : (
                <span className="mail-signatur-leer">Noch keine Signatur hinterlegt.</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Die gültige AGB-Fassung.
 *
 * Einmal eintragen, dann liegt sie in jedem neuen Vorgangsordner. Kora holt sie
 * beim Speichern von der Website und legt sie in der Materialablage ab – nicht
 * bei jeder Ordneranlage neu, sonst hinge das Anlegen eines Angebots daran,
 * dass die Website gerade erreichbar ist.
 */
function Agb({ daten }: { daten: EinstellungenStand }) {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [url, setUrl] = useState(daten.integrationen.agbUrl);
  const [fehler, setFehler] = useState('');

  const holen = useMutation({
    mutationFn: () => api.sende<{ toast: string }>('/einstellungen/agb', { url: url.trim() }),
    onSuccess: (a) => {
      setFehler('');
      void queryClient.invalidateQueries({ queryKey: ['einstellungen'] });
      toastZeigen(a.toast);
    },
    onError: (f) =>
      setFehler(f instanceof ApiFehler ? f.message : 'Die AGB konnte nicht geholt werden.'),
  });

  const stand = daten.integrationen.agbStand
    ? new Date(daten.integrationen.agbStand).toLocaleDateString('de-AT')
    : '';

  return (
    <div className="ein-unterabschnitt">
      <div className="ein-titel-klein">Allgemeine Geschäftsbedingungen</div>
      <div className="ein-hinweis">
        Kora legt die AGB in jeden neuen Vorgangsordner – in der Fassung, die zum Angebot gültig
        war. Ändern sich die AGB, wird hier die neue Adresse eingetragen; das ist der ganze Vorgang.
      </div>

      {daten.integrationen.agbDatei && (
        <div className="ein-statuszeile">
          <span className="ein-status an">
            {daten.integrationen.agbDatei}
            {stand ? ` · geholt am ${stand}` : ''}
          </span>
        </div>
      )}

      <label className="ein-feld">
        <span>Adresse der AGB-Datei</span>
        <input
          className="ein-eingabe"
          value={url}
          placeholder="https://eichenherz.at/wp-content/uploads/agb_….pdf"
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>

      {fehler && <div className="eh-fehler">{fehler}</div>}

      <div className="ein-vorlagen-aktionen">
        <button
          type="button"
          className="ein-knopf-prim"
          disabled={holen.isPending || !url.trim()}
          onClick={() => holen.mutate()}
        >
          {holen.isPending ? 'Wird geholt …' : 'AGB holen und hinterlegen'}
        </button>
      </div>
    </div>
  );
}
